/**
 * Topic Management Section Component
 * Admin interface for managing library topics
 */

import { useState } from "react";
import { Button } from "@components/common";
import { ConfirmModal } from "./Modal";
import { AddTopicModal } from "./AddTopicModal";
import { cn } from "@utils/cn";
import { toast } from "@stores/uiStore";
import {
  useAllLibraryTopics,
  useCreateTopic,
  useUpdateTopic,
  useDeleteTopic,
  useReorderTopics,
} from "@hooks/useLibraryTopics";
import type { LibraryTopicModel, CreateTopicRequest } from "@/types/models";

export function TopicManagementSection() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<LibraryTopicModel | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<LibraryTopicModel | null>(
    null,
  );

  // Queries and mutations
  const { data: topics, isLoading, error } = useAllLibraryTopics();
  const createMutation = useCreateTopic();
  const updateMutation = useUpdateTopic();
  const deleteMutation = useDeleteTopic();
  const reorderMutation = useReorderTopics();

  // Inline editing state
  const [editLabel, setEditLabel] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreateTopic = async (data: CreateTopicRequest) => {
    try {
      setCreateError(null);
      await createMutation.mutateAsync(data);
      setIsAddModalOpen(false);
      toast.success("Topic created", `"${data.label}" has been added.`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create topic";
      // Check for duplicate slug error (409 conflict)
      if (message.includes("already exists") || message.includes("409")) {
        setCreateError(`A topic with slug "${data.slug}" already exists.`);
      } else {
        setCreateError(message);
      }
      // Don't close modal - let user correct the error
    }
  };

  const handleStartEdit = (topic: LibraryTopicModel) => {
    setEditingTopic(topic);
    setEditLabel(topic.label);
    setEditDescription(topic.description || "");
  };

  const handleCancelEdit = () => {
    setEditingTopic(null);
    setEditLabel("");
    setEditDescription("");
  };

  const handleSaveEdit = async () => {
    if (!editingTopic) return;

    try {
      await updateMutation.mutateAsync({
        id: editingTopic.id,
        data: {
          label: editLabel.trim(),
          description: editDescription.trim() || null,
        },
      });
      handleCancelEdit();
      toast.success("Topic updated", `"${editLabel.trim()}" has been saved.`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update topic";
      toast.error("Update failed", message);
      // Don't cancel edit - let user retry
    }
  };

  const handleToggleActive = async (topic: LibraryTopicModel) => {
    try {
      await updateMutation.mutateAsync({
        id: topic.id,
        data: { isActive: !topic.isActive },
      });
      toast.success(
        topic.isActive ? "Topic deactivated" : "Topic activated",
        `"${topic.label}" is now ${topic.isActive ? "hidden" : "visible"} in filters.`,
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to update topic status";
      toast.error("Update failed", message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      // Try soft delete first (sets is_active to false)
      await deleteMutation.mutateAsync({ id: deleteTarget.id, force: false });
      toast.success(
        "Topic deactivated",
        `"${deleteTarget.label}" has been deactivated.`,
      );
      setDeleteTarget(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to deactivate topic";
      toast.error("Deactivation failed", message);
      // Don't close modal - let user see the error context
    }
  };

  const handleMoveUp = async (index: number) => {
    if (!topics || index <= 0) return;

    const newOrder = [...topics];
    [newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ];
    const topicIds = newOrder.map((t) => t.id);

    try {
      await reorderMutation.mutateAsync(topicIds);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to reorder topics";
      toast.error("Reorder failed", message);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (!topics || index >= topics.length - 1) return;

    const newOrder = [...topics];
    [newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ];
    const topicIds = newOrder.map((t) => t.id);

    try {
      await reorderMutation.mutateAsync(topicIds);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to reorder topics";
      toast.error("Reorder failed", message);
    }
  };

  if (!isExpanded) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Topic Management
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {topics ? `${topics.length} topics configured` : "Loading..."}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsExpanded(true)}
          >
            Manage Topics
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Topic Management
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage topics for categorizing library resources
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Topic
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(false)}
          >
            Collapse
          </Button>
        </div>
      </div>

      {/* Topics Table */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading topics...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">
            Error loading topics: {error.message}
          </div>
        ) : !topics || topics.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No topics configured. Click "Add Topic" to create one.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">
                  Order
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Label
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                  Resources
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {topics.map((topic, index) => (
                <tr
                  key={topic.id}
                  className={cn(
                    "hover:bg-gray-50 dark:hover:bg-gray-900/30",
                    !topic.isActive && "opacity-60",
                  )}
                >
                  {/* Order controls */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0 || reorderMutation.isPending}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleMoveDown(index)}
                        disabled={
                          index === topics.length - 1 ||
                          reorderMutation.isPending
                        }
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>

                  {/* Label */}
                  <td className="px-4 py-3">
                    {editingTopic?.id === topic.id ? (
                      <input
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        autoFocus
                      />
                    ) : (
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {topic.label}
                        </div>
                        {topic.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                            {topic.description}
                          </div>
                        )}
                      </div>
                    )}
                  </td>

                  {/* Slug */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-600 dark:text-gray-300">
                      {topic.slug}
                    </code>
                  </td>

                  {/* Resource count */}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {topic.resourceCount ?? 0}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(topic)}
                      disabled={updateMutation.isPending}
                      className={cn(
                        "inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors",
                        topic.isActive
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200",
                      )}
                    >
                      {topic.isActive ? "Active" : "Inactive"}
                    </button>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    {editingTopic?.id === topic.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={handleSaveEdit}
                          disabled={updateMutation.isPending}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-gray-500 hover:text-gray-700 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleStartEdit(topic)}
                          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                          title="Edit"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => setDeleteTarget(topic)}
                          className="text-red-500 hover:text-red-700"
                          title="Delete"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Topic Modal */}
      <AddTopicModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setCreateError(null);
        }}
        onSubmit={handleCreateTopic}
        isLoading={createMutation.isPending}
        error={createError}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Deactivate Topic"
        message={
          deleteTarget?.resourceCount && deleteTarget.resourceCount > 0
            ? `This topic is used by ${deleteTarget.resourceCount} resources. Deactivating it will hide it from filters but won't affect existing resources.`
            : "Are you sure you want to deactivate this topic? It will no longer appear in filters."
        }
        confirmLabel="Deactivate"
        variant="warning"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
